import React from "react";
import api from "../api";
import { motion, AnimatePresence } from "framer-motion";

const Gallery = ({ images, onLike, scrollToUpload, plan }) => {
  if (!images || images.length === 0) {
    return (
      <div className="bento-grid mt-2" dir="rtl">
        {/* Add New Card as first item */}
        <div
          className="row-span-1 bg-primary-fixed border-2 border-dashed border-primary/30 rounded-celebration d-flex flex-column align-items-center justify-content-center cursor-pointer transition-soft hover:bg-primary-dim shadow-sm"
          onClick={scrollToUpload}
        >
          <div className="w-12 h-12 rounded-circle bg-white d-flex align-items-center justify-content-center text-primary mb-3 shadow-sm scale-110">
            <span className="material-symbols-outlined">add</span>
          </div>
          <span className="font-bold text-on-primary-fixed small">إضافة أول مشاركة</span>
        </div>

        <motion.div
          className="col-span-2 row-span-1 d-flex align-items-center justify-content-center text-center p-4 bg-surface-variant rounded-celebration shadow-sm border border-primary-dim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <p className="text-on-surface-variant fs-5 mb-0 fw-medium">
            لسه مفيش مشاركات.. استنى أول ما الضيوف يرفعوا هتظهر هنا على طول! 📸
          </p>
        </motion.div>
      </div>
    );
  }

  const handleLike = async (id) => {
    try {
      await api.patch(`/api/media/reaction/${id}`);
      if (onLike) onLike();
    } catch (err) {
      console.error("Like error:", err);
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const item = {
    hidden: { opacity: 0, scale: 0.9, y: 15 },
    show: { opacity: 1, scale: 1, y: 0 }
  };

  return (
    <motion.div
      className="bento-grid mt-2"
      dir="rtl"
      variants={container}
      initial="hidden"
      animate="show"
    >
      {/* Add New Card always visible at start */}
      <div
          className="row-span-1 bg-primary-fixed border-2 border-dashed border-primary/30 rounded-celebration d-flex flex-column align-items-center justify-content-center cursor-pointer transition-soft hover:bg-primary-dim shadow-sm"
          onClick={scrollToUpload}
        >
          <div className="w-12 h-12 rounded-circle bg-white d-flex align-items-center justify-content-center text-primary mb-3 shadow-sm">
            <span className="material-symbols-outlined">add</span>
          </div>
          <span className="font-bold text-on-primary-fixed small">إضافة صورة جديدة</span>
      </div>

      <AnimatePresence>
        {images.map((media, idx) => {
          const id = media._id;
          // Asymmetric logic: make some items larger (only on larger screens)
          const isLarge = media.type === 'photo' && (idx % 5 === 1 || idx % 7 === 3);
          const isWide = media.type === 'photo' && (idx % 6 === 2);

          return (
            <div
              key={id}
              className={`${isLarge ? 'md:row-span-2' : 'row-span-1'} ${isWide ? 'md:col-span-2 md:row-span-2' : ''}`}
            >
              <motion.div
                className="card h-100 shadow-sm border-0 position-relative overflow-hidden rounded-celebration"
                variants={item}
                layout
                whileHover={{
                  y: -8,
                  boxShadow: "var(--shadow-lg)",
                  transition: { duration: 0.3 }
                }}
                onDoubleClick={() => handleLike(id)}
              >
                {media.type === "photo" && (
                  <div className="w-100 h-100 position-relative">
                    <img
                      src={media.url}
                      alt="صورة ضيف"
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                      loading="lazy"
                    />
                    {plan === 'free' && (
                      <div className="position-absolute bottom-0 end-0 m-2 px-2 py-1 bg-white bg-opacity-40 backdrop-blur-sm rounded-pill text-[10px] fw-black text-primary border border-white border-opacity-30 shadow-sm" style={{ zIndex: 4 }}>
                        يومك ✨
                      </div>
                    )}
                  </div>
                )}
                {media.type === "video" && (
                   <div className="w-100 h-100 bg-surface-container-high d-flex align-items-center justify-content-center relative">
                      <span className="material-symbols-outlined text-primary display-4">play_circle</span>
                      <video
                        src={media.url}
                        className="position-absolute w-100 h-100 opacity-0"
                        style={{ objectFit: "cover" }}
                      />
                      <div className="position-absolute bottom-0 end-0 m-3 px-3 py-1 bg-black bg-opacity-50 text-white rounded-pill small backdrop-blur-sm">
                        فيديو
                      </div>
                   </div>
                )}
                {media.type === "voice" && (
                  <div className="p-4 bg-tertiary-fixed h-100 d-flex flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="material-symbols-outlined text-primary display-6">mic</span>
                      <span className="text-[10px] font-bold text-primary opacity-60">رسالة صوتية</span>
                    </div>
                    <div className="mt-auto">
                       <p className="text-xs font-medium text-on-tertiary-fixed mb-2">بواسطة: {media.guestName}</p>
                       <audio src={media.url} controls className="w-100" style={{ maxHeight: '30px' }} />
                    </div>
                  </div>
                )}
                {media.type === "text" && (
                  <div className="p-4 bg-surface-container-lowest h-100 d-flex flex-column justify-content-between border-1 border-light">
                    <div>
                      <span className="material-symbols-outlined text-primary opacity-50 mb-2">format_quote</span>
                      <p className="mb-0 fs-6 fw-bold text-on-surface line-clamp-4 arabic-leading italic">"{media.content}"</p>
                    </div>
                  </div>
                )}

                {/* Hover Overlay */}
                <div className="position-absolute inset-0 bg-gradient-to-t from-black bg-opacity-60 via-transparent to-transparent opacity-0 transition-opacity hover-overlay d-flex flex-column justify-content-end p-3" style={{ zIndex: 5 }}>
                   <div className="d-flex justify-content-between align-items-center text-white">
                      <div className="d-flex align-items-center gap-2">
                         <span className="material-symbols-outlined fs-6">person</span>
                         <span className="small fw-bold">{media.guestName || "ضيف يومك"}</span>
                      </div>
                      <motion.button
                        className="btn btn-link text-white p-0 text-decoration-none d-flex align-items-center gap-1"
                        onClick={(e) => { e.stopPropagation(); handleLike(id); }}
                        whileTap={{ scale: 0.7 }}
                      >
                        ❤️ <span className="small">
                          {typeof media.reactions === 'number'
                            ? media.reactions
                            : (media.reactions?.heart || 0)}
                        </span>
                      </motion.button>
                   </div>
                </div>

                <style>{`
                  .card:hover .hover-overlay { opacity: 1 !important; }
                  .line-clamp-4 {
                    display: -webkit-box;
                    -webkit-line-clamp: 4;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                  }
                `}</style>
              </motion.div>
            </div>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );
};

export default Gallery;
